import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { fetchProject } from "../lib/api";
import { toEmbedSrc } from "../lib/embed";
import Reveal from "../components/Reveal";

const ProjectDetail = () => {
    const { id } = useParams();
    const [project, setProject] = useState(null);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        fetchProject(id)
            .then(setProject)
            .catch(() => setNotFound(true));
    }, [id]);

    if (notFound) {
        return (
            <main className="pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14">
                <p className="font-serif-display text-3xl">Project not found.</p>
                <Link
                    to="/work"
                    className="mt-6 inline-flex items-center gap-2 text-label text-muted-foreground hover:text-foreground"
                >
                    <ArrowLeft size={14} strokeWidth={1.25} /> Back to work
                </Link>
            </main>
        );
    }

    if (!project) {
        return (
            <main className="pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14">
                <p className="text-muted-foreground text-sm tracking-wider">
                    Loading…
                </p>
            </main>
        );
    }

    const embed = toEmbedSrc(project.embed_url);

    return (
        <main
            data-testid="project-detail-page"
            className="pt-32 sm:pt-40 pb-32 mx-auto max-w-[1500px] px-6 sm:px-10 lg:px-14"
        >
            <Reveal>
                <Link
                    to="/work"
                    data-testid="project-back"
                    className="inline-flex items-center gap-2 text-label text-muted-foreground hover:text-foreground mb-12"
                >
                    <ArrowLeft size={14} strokeWidth={1.25} /> All Work
                </Link>
            </Reveal>

            <Reveal delay={100}>
                <p className="text-label text-muted-foreground mb-4">
                    {project.category} — {project.year}
                </p>
                <h1
                    data-testid="project-title"
                    className="font-serif-display text-5xl sm:text-7xl tracking-tighter leading-[1.02] max-w-4xl"
                >
                    {project.title}
                </h1>
                <p className="mt-6 text-label text-foreground/70">
                    {project.role}
                </p>
            </Reveal>

            <Reveal delay={250}>
                <div className="mt-16 overflow-hidden bg-muted aspect-video cinematic-shadow">
                    {embed ? (
                        <iframe
                            title={project.title}
                            src={embed}
                            className="w-full h-full"
                            allow="autoplay; fullscreen; picture-in-picture"
                            allowFullScreen
                        />
                    ) : (
                        <img
                            src={project.thumbnail_url}
                            alt={project.title}
                            className="w-full h-full object-cover"
                        />
                    )}
                </div>
            </Reveal>

            <div className="mt-24 grid lg:grid-cols-12 gap-12">
                <Reveal className="lg:col-span-7">
                    <p className="text-label text-muted-foreground mb-4">
                        Notes
                    </p>
                    <p className="font-serif-display text-2xl sm:text-3xl leading-snug tracking-tight text-foreground/90">
                        {project.description || "—"}
                    </p>
                </Reveal>
                <Reveal delay={150} className="lg:col-span-4 lg:col-start-9">
                    <p className="text-label text-muted-foreground mb-4">
                        Credits
                    </p>
                    <dl className="space-y-4 text-sm">
                        <div className="flex justify-between border-b border-border pb-3">
                            <dt className="text-muted-foreground">Year</dt>
                            <dd>{project.year}</dd>
                        </div>
                        <div className="flex justify-between border-b border-border pb-3">
                            <dt className="text-muted-foreground">Role</dt>
                            <dd>{project.role}</dd>
                        </div>
                        <div className="flex justify-between border-b border-border pb-3">
                            <dt className="text-muted-foreground">Category</dt>
                            <dd>{project.category}</dd>
                        </div>
                    </dl>
                    {project.awards?.length > 0 && (
                        <div className="mt-8">
                            <p className="text-label text-muted-foreground mb-4">
                                Recognition
                            </p>
                            <ul className="space-y-3 text-sm">
                                {project.awards.map((a) => (
                                    <li
                                        key={a}
                                        className="italic text-foreground/80 leading-relaxed"
                                    >
                                        — {a}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </Reveal>
            </div>

            {project.gallery_urls?.length > 0 && (
                <div
                    data-testid="project-gallery"
                    className="mt-24 grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                    {project.gallery_urls.map((src, i) => (
                        <Reveal key={src + i} delay={(i % 2) * 100}>
                            <div className="overflow-hidden bg-muted aspect-[4/3]">
                                <img
                                    src={src}
                                    loading="lazy"
                                    alt=""
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        </Reveal>
                    ))}
                </div>
            )}
        </main>
    );
};

export default ProjectDetail;
